import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Edit, ArrowUp, ArrowDown, X, Contact, ListChecks, Users, Settings } from 'lucide-react';
import UserMultiSelect from './UserMultiSelect';

const FIELD_TYPES = [
  { value: 'short_text', label: 'Kurzer Text' },
  { value: 'long_text', label: 'Langer Text' },
  { value: 'number', label: 'Zahl' },
  { value: 'email', label: 'E-Mail' },
  { value: 'phone', label: 'Telefon' },
  { value: 'url', label: 'URL' },
  { value: 'date', label: 'Datum' },
  { value: 'date_optional_year', label: 'Datum (Jahr optional)' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'multiselect', label: 'Mehrfachauswahl' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'yes_no', label: 'Ja/Nein' },
  { value: 'address', label: 'Adresse' },
  { value: 'country_dropdown', label: 'Land-Dropdown' },
  { value: 'user_select', label: 'User-Auswahl' },
];

const AdminKontakte = () => {
  const [tab, setTab] = useState('fields');
  const [kontaktArt, setKontaktArt] = useState('extern');
  const [fields, setFields] = useState([]);
  const [dropdowns, setDropdowns] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Field dialog
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState({});

  // Dropdown dialog
  const [showDdDialog, setShowDdDialog] = useState(false);
  const [editingDd, setEditingDd] = useState(null);
  const [ddForm, setDdForm] = useState({ key: '', label: '', values: [] });
  const [newValueInput, setNewValueInput] = useState('');

  // Group dialog
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '', member_ids: [] });

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ddRes, grpRes, usrRes] = await Promise.all([
        axios.get('/kontakte/dropdowns'),
        axios.get('/kontakte/groups'),
        axios.get('/kontakte/_/users'),
      ]);
      setDropdowns(ddRes.data || []);
      setGroups(grpRes.data || []);
      setUsers(usrRes.data || []);
    } catch (err) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, []);

  const loadFields = useCallback(async (art) => {
    try {
      const r = await axios.get(`/kontakte/fields/${art}`);
      const sorted = (r.data.fields || []).slice().sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
      setFields(sorted);
    } catch (err) { toast.error('Felder konnten nicht geladen werden'); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadFields(kontaktArt); }, [kontaktArt, loadFields]);

  // ===== FIELDS =====
  const openNewField = () => {
    setEditingField(null);
    setFieldForm({ label: '', type: 'short_text', required: false, placeholder: '', help_text: '', default_value: '', dropdown_key: '', searchable: false, filterable: false, in_table: false, readonly: false });
    setShowFieldDialog(true);
  };

  const openEditField = (f, idx) => {
    setEditingField(idx);
    setFieldForm({ ...f });
    setShowFieldDialog(true);
  };

  const saveField = () => {
    if (!fieldForm.label?.trim()) { toast.error('Label erforderlich'); return; }
    if (['dropdown', 'multiselect'].includes(fieldForm.type) && !fieldForm.dropdown_key) {
      toast.error('Dropdown-Key erforderlich für Dropdown/Multiselect');
      return;
    }
    const newField = { ...fieldForm };
    if (editingField !== null) {
      const upd = [...fields];
      upd[editingField] = { ...upd[editingField], ...newField };
      setFields(upd);
    } else {
      setFields([...fields, newField]);
    }
    setShowFieldDialog(false);
  };

  const removeField = (idx) => {
    if (!window.confirm('Feld entfernen? Bestehende Kontaktwerte bleiben in der DB erhalten, werden aber nicht mehr angezeigt.')) return;
    setFields(fields.filter((_, i) => i !== idx));
  };

  const moveField = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= fields.length) return;
    const upd = [...fields];
    [upd[idx], upd[ni]] = [upd[ni], upd[idx]];
    setFields(upd);
  };

  const saveFieldsConfig = async () => {
    try {
      const payload = fields.map((f, i) => ({ ...f, sort_order: i }));
      await axios.put(`/kontakte/fields/${kontaktArt}`, { fields: payload });
      toast.success('Felder gespeichert');
      await loadFields(kontaktArt);
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  // ===== DROPDOWNS =====
  const openNewDd = () => {
    setEditingDd(null);
    setDdForm({ key: '', label: '', values: [] });
    setNewValueInput('');
    setShowDdDialog(true);
  };

  const openEditDd = (dd) => {
    setEditingDd(dd);
    setDdForm({ key: dd.key, label: dd.label, values: [...dd.values] });
    setNewValueInput('');
    setShowDdDialog(true);
  };

  const addDdValue = () => {
    const v = newValueInput.trim();
    if (!v) return;
    if (ddForm.values.includes(v)) return;
    setDdForm(p => ({ ...p, values: [...p.values, v] }));
    setNewValueInput('');
  };

  const saveDd = async () => {
    if (!ddForm.label?.trim()) { toast.error('Label erforderlich'); return; }
    try {
      if (editingDd) {
        await axios.patch(`/kontakte/dropdowns/${editingDd.key}`, { label: ddForm.label, values: ddForm.values });
        toast.success('Dropdown aktualisiert');
      } else {
        if (!ddForm.key?.trim() || !/^[a-z][a-z0-9_]*$/.test(ddForm.key)) { toast.error('Key muss lowercase a-z, 0-9, _ sein'); return; }
        await axios.post('/kontakte/dropdowns', ddForm);
        toast.success('Dropdown erstellt');
      }
      setShowDdDialog(false);
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteDd = async (dd) => {
    if (!window.confirm(`Dropdown "${dd.key}" wirklich löschen?`)) return;
    try {
      await axios.delete(`/kontakte/dropdowns/${dd.key}`);
      toast.success('Gelöscht');
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  // ===== GROUPS =====
  const openNewGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '', member_ids: [] });
    setShowGroupDialog(true);
  };

  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, description: g.description || '', member_ids: g.member_ids || [] });
    setShowGroupDialog(true);
  };

  const saveGroup = async () => {
    if (!groupForm.name?.trim()) { toast.error('Name erforderlich'); return; }
    try {
      if (editingGroup) {
        await axios.patch(`/kontakte/groups/${editingGroup.id}`, groupForm);
        toast.success('Gruppe aktualisiert');
      } else {
        await axios.post('/kontakte/groups', groupForm);
        toast.success('Gruppe erstellt');
      }
      setShowGroupDialog(false);
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Gruppe "${g.name}" löschen?`)) return;
    try {
      await axios.delete(`/kontakte/groups/${g.id}`);
      toast.success('Gelöscht');
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-500">Lade...</div>;

  return (
    <div className="space-y-4" data-testid="admin-kontakte-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Contact className="w-6 h-6 text-[#e30613]" /> Kontakt-Verwaltung</h1>
        <p className="text-sm text-gray-500 mt-1">Felder, Dropdowns und Gruppen konfigurieren</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="fields" data-testid="tab-fields"><Settings className="w-4 h-4 mr-1" /> Felder</TabsTrigger>
          <TabsTrigger value="dropdowns" data-testid="tab-dropdowns"><ListChecks className="w-4 h-4 mr-1" /> Dropdowns</TabsTrigger>
          <TabsTrigger value="groups" data-testid="tab-groups"><Users className="w-4 h-4 mr-1" /> Gruppen</TabsTrigger>
        </TabsList>

        {/* FIELDS */}
        <TabsContent value="fields">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <CardTitle className="text-base">Feld-Builder</CardTitle>
                  <CardDescription>{fields.length} Felder konfiguriert</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Tabs value={kontaktArt} onValueChange={setKontaktArt}>
                    <TabsList>
                      <TabsTrigger value="extern" data-testid="art-extern">Extern</TabsTrigger>
                      <TabsTrigger value="intern" data-testid="art-intern">Intern</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <Button size="sm" variant="outline" onClick={openNewField} data-testid="add-field-btn"><Plus className="w-4 h-4 mr-1" /> Feld</Button>
                  <Button size="sm" onClick={saveFieldsConfig} data-testid="save-fields-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Keine Felder. Klicke "Feld" zum Hinzufügen.</p>
              ) : (
                <div className="space-y-2">
                  {fields.map((f, i) => (
                    <div key={f.id || f.key + i} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`field-row-${i}`}>
                      <div className="flex flex-col gap-0.5">
                        <Button size="icon" variant="ghost" className="h-5 w-5" disabled={i === 0} onClick={() => moveField(i, -1)}><ArrowUp className="w-3 h-3" /></Button>
                        <Button size="icon" variant="ghost" className="h-5 w-5" disabled={i === fields.length - 1} onClick={() => moveField(i, 1)}><ArrowDown className="w-3 h-3" /></Button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{f.label}</span>
                          {f.required && <Badge variant="destructive" className="text-[10px]">Pflicht</Badge>}
                          {f.searchable && <Badge variant="outline" className="text-[10px]">suchbar</Badge>}
                          {f.filterable && <Badge variant="outline" className="text-[10px]">filterbar</Badge>}
                          {f.in_table && <Badge variant="outline" className="text-[10px]">Tabelle</Badge>}
                          {f.readonly && <Badge variant="outline" className="text-[10px]">readonly</Badge>}
                        </div>
                        <span className="text-xs text-gray-500">
                          {FIELD_TYPES.find(t => t.value === f.type)?.label || f.type}
                          {f.dropdown_key && ` · ${f.dropdown_key}`}
                          {' · key='}<code>{f.key}</code>
                        </span>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditField(f, i)}><Edit className="w-3 h-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeField(i)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DROPDOWNS */}
        <TabsContent value="dropdowns">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Dropdowns</CardTitle>
                <CardDescription>{dropdowns.length} Listen</CardDescription>
              </div>
              <Button size="sm" onClick={openNewDd} data-testid="add-dropdown-btn"><Plus className="w-4 h-4 mr-1" /> Dropdown</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {dropdowns.map(dd => (
                <div key={dd.key} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`dropdown-row-${dd.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{dd.label}</span>
                      <code className="text-[10px] bg-gray-200 dark:bg-gray-700 px-1 rounded">{dd.key}</code>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">{dd.values.slice(0, 6).join(', ')}{dd.values.length > 6 && ` … +${dd.values.length - 6}`}</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditDd(dd)} data-testid={`edit-dd-${dd.key}`}><Edit className="w-3 h-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteDd(dd)} data-testid={`delete-dd-${dd.key}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
              {dropdowns.length === 0 && <p className="text-center text-gray-500 py-6">Keine Dropdowns konfiguriert</p>}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GROUPS */}
        <TabsContent value="groups">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Kontaktgruppen</CardTitle>
                <CardDescription>Flache Gruppen für Sichtbarkeit (z.B. Direktoren, Sponsoring)</CardDescription>
              </div>
              <Button size="sm" onClick={openNewGroup} data-testid="add-group-btn"><Plus className="w-4 h-4 mr-1" /> Gruppe</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {groups.map(g => (
                <div key={g.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-testid={`group-row-${g.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{g.name}</p>
                    {g.description && <p className="text-xs text-gray-500">{g.description}</p>}
                    <p className="text-xs text-gray-400 mt-0.5">{(g.member_ids || []).length} Mitglieder</p>
                  </div>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEditGroup(g)}><Edit className="w-3 h-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => deleteGroup(g)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
              {groups.length === 0 && <p className="text-center text-gray-500 py-6">Keine Gruppen vorhanden</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* FIELD DIALOG */}
      <Dialog open={showFieldDialog} onOpenChange={setShowFieldDialog}>
        <DialogContent className="max-w-lg" data-testid="field-dialog">
          <DialogHeader>
            <DialogTitle>{editingField !== null ? 'Feld bearbeiten' : 'Neues Feld'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label>Label *</Label>
              <Input value={fieldForm.label || ''} onChange={e => setFieldForm(p => ({ ...p, label: e.target.value }))} data-testid="field-label-input" />
            </div>
            <div>
              <Label>Key (intern)</Label>
              <Input value={fieldForm.key || ''} onChange={e => setFieldForm(p => ({ ...p, key: e.target.value }))} placeholder="auto aus Label" disabled={editingField !== null} />
              <p className="text-xs text-gray-400 mt-0.5">Wird beim Speichern automatisch generiert, falls leer. Nicht änderbar nach Erstellung.</p>
            </div>
            <div>
              <Label>Feldtyp</Label>
              <Select value={fieldForm.type} onValueChange={v => setFieldForm(p => ({ ...p, type: v }))}>
                <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {['dropdown', 'multiselect'].includes(fieldForm.type) && (
              <div>
                <Label>Dropdown-Key *</Label>
                <Select value={fieldForm.dropdown_key || '_none'} onValueChange={v => setFieldForm(p => ({ ...p, dropdown_key: v === '_none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Dropdown wählen..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {dropdowns.map(d => <SelectItem key={d.key} value={d.key}>{d.label} ({d.key})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Placeholder</Label><Input value={fieldForm.placeholder || ''} onChange={e => setFieldForm(p => ({ ...p, placeholder: e.target.value }))} /></div>
              <div><Label>Standardwert</Label><Input value={fieldForm.default_value || ''} onChange={e => setFieldForm(p => ({ ...p, default_value: e.target.value }))} /></div>
            </div>
            <div><Label>Hilfetext</Label><Input value={fieldForm.help_text || ''} onChange={e => setFieldForm(p => ({ ...p, help_text: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              {[
                ['required', 'Pflichtfeld'],
                ['searchable', 'Suchbar'],
                ['filterable', 'Filterbar'],
                ['in_table', 'In Tabelle'],
                ['readonly', 'Nur lesen'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center gap-2">
                  <Switch checked={!!fieldForm[key]} onCheckedChange={v => setFieldForm(p => ({ ...p, [key]: v }))} data-testid={`field-${key}-switch`} />
                  <Label className="text-sm">{label}</Label>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowFieldDialog(false)}>Abbrechen</Button>
              <Button onClick={saveField} data-testid="save-field-btn"><Save className="w-4 h-4 mr-1" /> Übernehmen</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DROPDOWN DIALOG */}
      <Dialog open={showDdDialog} onOpenChange={setShowDdDialog}>
        <DialogContent data-testid="dd-dialog">
          <DialogHeader>
            <DialogTitle>{editingDd ? 'Dropdown bearbeiten' : 'Neues Dropdown'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Label</Label><Input value={ddForm.label} onChange={e => setDdForm(p => ({ ...p, label: e.target.value }))} data-testid="dd-label-input" /></div>
            <div>
              <Label>Key {editingDd && <span className="text-xs text-gray-400">(nicht änderbar)</span>}</Label>
              <Input value={ddForm.key} onChange={e => setDdForm(p => ({ ...p, key: e.target.value.toLowerCase() }))} disabled={!!editingDd} placeholder="z.B. branche" data-testid="dd-key-input" />
            </div>
            <div>
              <Label>Werte</Label>
              <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                {ddForm.values.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 p-1.5 bg-gray-50 dark:bg-gray-800 rounded">{v}</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setDdForm(p => ({ ...p, values: p.values.filter((_, j) => j !== i) }))}><X className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newValueInput} onChange={e => setNewValueInput(e.target.value)} placeholder="Neuer Wert..." onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDdValue())} data-testid="dd-new-value-input" />
                <Button size="sm" onClick={addDdValue}><Plus className="w-3 h-3" /></Button>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowDdDialog(false)}>Abbrechen</Button>
              <Button onClick={saveDd} data-testid="save-dd-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* GROUP DIALOG */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent data-testid="group-dialog">
          <DialogHeader><DialogTitle>{editingGroup ? 'Gruppe bearbeiten' : 'Neue Gruppe'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={groupForm.name} onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))} data-testid="group-name-input" /></div>
            <div><Label>Beschreibung</Label><Textarea value={groupForm.description} onChange={e => setGroupForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
            <div>
              <Label>Mitglieder</Label>
              <UserMultiSelect users={users} selectedIds={groupForm.member_ids} onChange={(ids) => setGroupForm(p => ({ ...p, member_ids: ids }))} testId="group-members" placeholder="Nach Name suchen…" />
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Abbrechen</Button>
              <Button onClick={saveGroup} data-testid="save-group-btn"><Save className="w-4 h-4 mr-1" /> Speichern</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminKontakte;
